import React from 'react';
import { Alert, Button, Spin } from 'antd';
import type { TypedEntry } from './monetizationTabState';

export const SKIPPED_TITLE = 'Not available right now';
/** Shown when the backend gave no reason of its own. */
export const SKIPPED_HINT =
  'No data for this range yet (the BigQuery tables may not be built), or the query failed; see the server log.';

interface Props<T> {
  entry: TypedEntry<T>;
  onRetry?: () => void;
  children: (data: T) => React.ReactNode;
}

/**
 * What a backend tab shows: a spinner while it loads, the server's reason when the call failed
 * (a 400 for a range over 92 days carries its message), an info Alert when there is no data
 * (the backend's own message, else a neutral hint: skipped covers an unbuilt table and a failed
 * query alike), else the content.
 */
export function TabBody<T>({ entry, onRetry, children }: Props<T>): React.ReactElement {
  if (entry.status === 'idle' || entry.status === 'loading') {
    return (
      <div data-state="loading" style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (entry.status === 'failed') {
    return (
      <div data-alert="error">
        <Alert
          type="error"
          showIcon
          message="Couldn't load this tab"
          description={entry.message}
          action={
            onRetry ? (
              <Button size="small" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }
  const { data, skipped } = entry.result;
  if (data === null) {
    return (
      <div data-alert="skipped">
        <Alert
          type="info"
          showIcon
          message={SKIPPED_TITLE}
          description={
            <>
              <div data-skipped-message="1">{skipped || SKIPPED_HINT}</div>
            </>
          }
        />
      </div>
    );
  }
  return <>{children(data)}</>;
}
